/* 인증 세션 — React Context + useSession(). plan.md §4.
   ★ 동결 인터페이스(S2~S4 변경 금지):
       useSession() => {
         ready, mode:'live'|'fake', isDeveloper, email,
         getIdToken()      => Promise<string|null>,
         getCredentials()  => Promise<{accessKeyId,secretAccessKey,sessionToken,region}|null>,
         login(email, pw)  => Promise<void>   // 실패/비-developer면 throw
         logout()          => void,
       }
   S1 = fake 구현. S2 = 이 파일을 real 로 전환 — live 분기는 cognito.js(SRP 로그인 →
   developer 그룹 확인 → Identity Pool STS)에 위임. env 가 fake 면 S1 fake 경로 유지. */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isFakeMode, AWS_REGION } from '../config/env.js';
import { cognito } from './cognito.js';

const SessionContext = createContext(null);
const FAKE_KEY = 'adm.fakeSession';   // 탭 세션 동안 fake 로그인 유지
const REFRESH_POLL_MS = 60 * 1000;    // live: 토큰 신선도 점검(만료 임박 시 cognito 가 갱신)

export function SessionProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState(null);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const mode = isFakeMode ? 'fake' : 'live';

  // 부트스트랩: fake = sessionStorage 복원. live = cognito.restore()(토큰 복원·갱신).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isFakeMode) {
        try {
          const s = JSON.parse(sessionStorage.getItem(FAKE_KEY) || 'null');
          if (s && s.email) { setEmail(s.email); setIsDeveloper(true); }
        } catch (e) { /* ignore */ }
      } else {
        try {
          const s = await cognito.restore();
          if (!cancelled && s && s.email) { setEmail(s.email); setIsDeveloper(true); }
        } catch (e) { /* 복원 실패 → 미로그인 상태로 진입 */ }
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (em, pw) => {
    if (isFakeMode) {
      if (!em || !pw) throw new Error('이메일과 비밀번호를 입력하세요.');
      // fake: 자격 검증 없음 — 개발자 권한으로 통과시켜 데모/디자인 검토를 지원.
      try { sessionStorage.setItem(FAKE_KEY, JSON.stringify({ email: em })); } catch (e) { /* ignore */ }
      setEmail(em); setIsDeveloper(true);
      return;
    }
    // live: SRP 로그인 → developer 그룹 확인(아니면 cognito 가 throw) → STS 준비.
    if (!em || !pw) throw new Error('이메일과 비밀번호를 입력하세요.');
    const s = await cognito.login(em.trim(), pw);
    setEmail(s.email); setIsDeveloper(s.isDeveloper);
  }, []);

  const logout = useCallback(() => {
    if (isFakeMode) {
      try { sessionStorage.removeItem(FAKE_KEY); } catch (e) { /* ignore */ }
    } else {
      cognito.logout();
    }
    setEmail(null); setIsDeveloper(false);
  }, []);

  // 신선한 ID token. fake = 더미. live = cognito(만료 임박 시 갱신, 실패면 null).
  const getIdToken = useCallback(async () => {
    if (isFakeMode) return email ? 'fake-id-token' : null;
    return cognito.getIdToken();
  }, [email]);

  // S3 호출용 STS 임시 자격. fake = 더미. live = Identity Pool swap(캐시·재발급).
  const getCredentials = useCallback(async () => {
    if (isFakeMode) {
      return email
        ? { accessKeyId: 'FAKE', secretAccessKey: 'FAKE', sessionToken: 'FAKE', region: AWS_REGION }
        : null;
    }
    return cognito.getCredentials();
  }, [email]);

  // live: 로그인 동안 토큰 신선도 주기 점검. getIdToken 이 만료 5분 전 선제 갱신하고
  // 갱신 불가(refresh token 만료/소실) 시 null → 자동 로그아웃(plan §4: 갱신 실패 → 재로그인).
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  useEffect(() => {
    if (isFakeMode || !email) return;
    const id = setInterval(async () => {
      const tok = await cognito.getIdToken();
      if (!tok) logoutRef.current();
    }, REFRESH_POLL_MS);
    return () => clearInterval(id);
  }, [email]);

  const value = { ready, mode, isDeveloper, email, getIdToken, getCredentials, login, logout };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession 은 <SessionProvider> 내부에서만 사용할 수 있습니다.');
  return ctx;
}

export default SessionProvider;
